import * as RAlert from '@radix-ui/react-alert-dialog';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { TextInput } from './Field';

/**
 * Destructive-confirm dialog. For irreversible actions (page delete, PDPL erasure,
 * last-admin guards) pass `confirmPhrase` to require the user to type it (spec §16 / §11.5).
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  tone = 'primary',
  confirmPhrase,
  onConfirm,
  loading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'primary' | 'danger';
  confirmPhrase?: string;
  onConfirm: () => void;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');
  const phraseOk = !confirmPhrase || typed.trim() === confirmPhrase;

  // Clear the typed phrase whenever the dialog closes. Without this, a dialog
  // that was opened, typed into, and cancelled comes back with its guard already
  // satisfied — the second confirm would be a single click on a control whose
  // entire purpose is that it cannot be a single click. It matters most for the
  // dialogs mounted once per screen and reopened per row (a send, an erasure),
  // where the phrase from the LAST row would unlock the next one.
  useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  return (
    <RAlert.Root open={open} onOpenChange={onOpenChange}>
      <RAlert.Portal>
        <RAlert.Overlay className="fixed inset-0 z-90 bg-ink-navy/40" />
        <RAlert.Content
          // See Dialog.tsx: opt out of the Radix description warning when none is supplied.
          {...(description ? {} : { 'aria-describedby': undefined })}
          className="fixed inset-s-1/2 top-1/2 z-91 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border-[0.5px] border-hairline bg-canvas-paper p-lg rtl:translate-x-1/2"
        >
          <RAlert.Title className="wrap-break-word text-title text-ink-deep">{title}</RAlert.Title>
          {description && (
            <RAlert.Description className="mt-sm text-small text-ink-mid">
              {description}
            </RAlert.Description>
          )}
          {confirmPhrase && (
            <div className="mt-md">
              <TextInput
                label={t('ui.typeToConfirm', { phrase: confirmPhrase })}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoFocus
              />
            </div>
          )}
          <div className="mt-lg flex justify-end gap-sm">
            <RAlert.Cancel asChild>
              <Button variant="ghost">{t('common.cancel')}</Button>
            </RAlert.Cancel>
            <Button
              variant={tone === 'danger' ? 'danger' : 'primary'}
              loading={loading}
              disabled={!phraseOk}
              onClick={onConfirm}
            >
              {confirmLabel ?? t('common.confirm')}
            </Button>
          </div>
        </RAlert.Content>
      </RAlert.Portal>
    </RAlert.Root>
  );
}
